import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, User, Mail, Phone, MapPin, 
  Check, X, Trash2, LogOut, Home, MessageSquare,
  DollarSign, Filter, RefreshCw, TrendingUp, AlertCircle, Ban, Unlock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle, AlertDialogTrigger 
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { API, useAuth, authFetch } from '@/App';
import { DashboardStats } from '@/components/DashboardStats';
import { ScheduleEditor } from '@/components/ScheduleEditor';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5b06bfd4-5798-4290-968c-c27d6c6d2c81/artifacts/krjgv0n0_LOGO01.png";

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  confirmed: 'bg-green-100 text-green-800 border-green-200',
  completed: 'bg-blue-100 text-blue-800 border-blue-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const PAYMENT_STATUS_COLORS = {
  unpaid: 'bg-orange-100 text-orange-800 border-orange-200',
  paid: 'bg-emerald-100 text-emerald-800 border-emerald-200'
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('access_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({
    total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0, revenue: 0, pending_revenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockDate, setBlockDate] = useState(null);
  const [blockTime, setBlockTime] = useState('');
  const [blockTimeFrom, setBlockTimeFrom] = useState('');
  const [blockTimeTo, setBlockTimeTo] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blockMode, setBlockMode] = useState('day');
  const [blockDateRange, setBlockDateRange] = useState({ from: null, to: null });
  const [blockPickedDays, setBlockPickedDays] = useState([]);
  const [selectedUnblockIds, setSelectedUnblockIds] = useState([]);
  // Schedule
  const [schedule, setSchedule] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/bookings`, { 
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch bookings');
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    }
  }, []);

  const fetchFeedback = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/feedback`, { 
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch feedback');
      const data = await response.json();
      setFeedback(data);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/bookings/stats`, { 
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchBlockedSlots = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/blocked-slots`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch blocked slots');
      const data = await response.json();
      setBlockedSlots(data);
    } catch (error) {
      console.error('Failed to fetch blocked slots:', error);
    }
  }, []);

  const fetchSchedule = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/schedule`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch schedule');
      const data = await response.json();
      setSchedule(data);
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchBookings(), fetchFeedback(), fetchStats(), fetchBlockedSlots(), fetchSchedule()]);
    setRefreshing(false);
    setLoading(false);
  }, [fetchBookings, fetchFeedback, fetchStats, fetchBlockedSlots, fetchSchedule]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const response = await fetch(`${API}/admin/schedule`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(schedule)
      });
      if (!response.ok) throw new Error('Failed to save');
      const data = await response.json();
      setSchedule(data);
      toast.success('Schedule saved!');
    } catch (error) {
      toast.error('Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const updateScheduleDay = (dayName, field, value) => {
    setSchedule(prev => prev.map(d => 
      d.day === dayName ? { ...d, [field]: value } : d
    ));
  };

  const ALL_TIME_SLOTS = ["10:00 AM","11:00 AM","12:00 PM","1:00 PM","2:00 PM","3:00 PM","4:00 PM","5:00 PM","6:00 PM","7:00 PM","8:00 PM","9:00 PM"];

  const handleBlockSlot = async () => {
    if (blockMode === 'daterange') {
      if (!blockDateRange.from || !blockDateRange.to) { toast.error('Select start and end date'); return; }
    } else if (blockMode === 'pickdays') {
      if (blockPickedDays.length === 0) { toast.error('Pick at least one day'); return; }
    } else if (blockMode !== 'daterange' && blockMode !== 'pickdays' && !blockDate) {
      toast.error('Select a date'); return;
    }
    if (blockMode === 'time' && !blockTime) { toast.error('Select a time slot'); return; }
    if (blockMode === 'range' && (!blockTimeFrom || !blockTimeTo)) { toast.error('Select both From and To times'); return; }
    if (blockTimeFrom && blockTimeTo) {
      const fi = ALL_TIME_SLOTS.indexOf(blockTimeFrom);
      const ti = ALL_TIME_SLOTS.indexOf(blockTimeTo);
      if (fi < 0 || ti < 0 || fi > ti) { toast.error('"From" must be before "To"'); return; }
    }

    try {
      const bulkBody = () => {
        const b = { reason: blockReason || null };
        if (blockTimeFrom && blockTimeTo) { b.from_time = blockTimeFrom; b.to_time = blockTimeTo; }
        return b;
      };

      const callBulk = async (dates) => {
        const resp = await fetch(`${API}/admin/blocked-slots/bulk`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({ dates, ...bulkBody() })
        });
        if (!resp.ok) throw new Error('Failed');
        return await resp.json();
      };

      if (blockMode === 'time') {
        const resp = await fetch(`${API}/admin/blocked-slots`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({ date: format(blockDate, 'yyyy-MM-dd'), time: blockTime, reason: blockReason || null })
        });
        if (!resp.ok) { const e = await resp.json(); throw new Error(e.detail || 'Failed'); }
        toast.success(`Blocked ${blockTime}`);
      } else if (blockMode === 'range') {
        const r = await callBulk([format(blockDate, 'yyyy-MM-dd')]);
        toast.success(`Blocked ${r.count} slot(s): ${blockTimeFrom} – ${blockTimeTo}`);
      } else if (blockMode === 'day') {
        if (blockTimeFrom && blockTimeTo) {
          const r = await callBulk([format(blockDate, 'yyyy-MM-dd')]);
          toast.success(`Blocked ${r.count} slot(s)`);
        } else {
          const resp = await fetch(`${API}/admin/blocked-slots`, {
            method: 'POST', headers: getAuthHeaders(),
            body: JSON.stringify({ date: format(blockDate, 'yyyy-MM-dd'), time: null, reason: blockReason || null })
          });
          if (!resp.ok) { const e = await resp.json(); throw new Error(e.detail || 'Failed'); }
          toast.success('Entire day blocked');
        }
      } else if (blockMode === 'daterange') {
        const dates = [];
        const cur = new Date(blockDateRange.from); const end = new Date(blockDateRange.to);
        while (cur <= end) { dates.push(format(new Date(cur), 'yyyy-MM-dd')); cur.setDate(cur.getDate() + 1); }
        const r = await callBulk(dates);
        toast.success(`Blocked ${r.count} slot(s)`);
      } else if (blockMode === 'pickdays') {
        const dates = blockPickedDays.map(d => format(d, 'yyyy-MM-dd'));
        const r = await callBulk(dates);
        toast.success(`Blocked ${r.count} slot(s)`);
      } else if (blockMode === 'week' || blockMode === 'month') {
        const dates = [];
        const start = new Date(blockDate);
        if (blockMode === 'week') {
          const dow = start.getDay();
          const mon = new Date(start); mon.setDate(start.getDate() - (dow === 0 ? 6 : dow - 1));
          for (let i = 0; i < 7; i++) { const d = new Date(mon); d.setDate(mon.getDate() + i); dates.push(format(d, 'yyyy-MM-dd')); }
        } else {
          const y = start.getFullYear(), m = start.getMonth(), dim = new Date(y, m + 1, 0).getDate();
          for (let i = 1; i <= dim; i++) dates.push(format(new Date(y, m, i), 'yyyy-MM-dd'));
        }
        const r = await callBulk(dates);
        toast.success(`Blocked ${r.count} slot(s)`);
      }

      setBlockDate(null); setBlockTime(''); setBlockTimeFrom(''); setBlockTimeTo('');
      setBlockReason(''); setBlockDateRange({ from: null, to: null }); setBlockPickedDays([]);
      setSelectedUnblockIds([]);
      fetchBlockedSlots();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUnblockSlot = async (slotId) => {
    try {
      const response = await fetch(`${API}/admin/blocked-slots/${slotId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to unblock');
      toast.success('Slot unblocked');
      fetchBlockedSlots();
    } catch (error) {
      toast.error('Failed to unblock slot');
    }
  };

  const handleBulkUnblock = async () => {
    if (selectedUnblockIds.length === 0) return;
    try {
      let successCount = 0;
      for (const slotId of selectedUnblockIds) {
        const response = await fetch(`${API}/admin/blocked-slots/${slotId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        if (response.ok) successCount++;
      }
      toast.success(`Unblocked ${successCount} slot(s)`);
      setSelectedUnblockIds([]);
      fetchBlockedSlots();
    } catch (error) {
      toast.error('Failed to unblock some slots');
    }
  };

  const toggleUnblockSelect = (slotId) => {
    setSelectedUnblockIds(prev => 
      prev.includes(slotId) ? prev.filter(id => id !== slotId) : [...prev, slotId]
    );
  };

  const selectAllUnblock = () => {
    if (selectedUnblockIds.length === blockedSlots.length) {
      setSelectedUnblockIds([]);
    } else {
      setSelectedUnblockIds(blockedSlots.map(s => s.slot_id));
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        
        body: JSON.stringify({ status })
      });
      if (!response.ok) throw new Error('Failed to update');
      toast.success(`Booking ${status}! ${status === 'confirmed' ? 'Date is now locked on calendar.' : ''}`);
      loadAllData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const updatePaymentStatus = async (bookingId, paymentStatus, paymentMethod = null) => {
    try {
      const body = { payment_status: paymentStatus };
      if (paymentMethod) body.payment_method = paymentMethod;
      
      const response = await fetch(`${API}/admin/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('Failed to update');
      toast.success('Payment status updated');
      loadAllData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const deleteBooking = async (bookingId) => {
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}`, {
        method: 'DELETE',
        
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete');
      toast.success('Booking deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const toggleFeedbackApproval = async (feedbackId) => {
    try {
      const response = await fetch(`${API}/admin/feedback/${feedbackId}`, {
        method: 'PATCH',
        
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to update');
      const data = await response.json();
      toast.success(data.message);
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to update feedback');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    try {
      const response = await fetch(`${API}/admin/feedback/${feedbackId}`, {
        method: 'DELETE',
        
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete');
      toast.success('Feedback deleted');
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to delete feedback');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const filteredBookings = useMemo(() => 
    statusFilter === 'all' 
      ? bookings 
      : bookings.filter(b => b.status === statusFilter),
    [bookings, statusFilter]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-healing-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-healing-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-healing-burgundy-light font-body">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-healing-cream" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-healing-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Healing Hands" className="h-10 w-auto" />
              <div>
                <h1 className="font-heading text-xl text-healing-burgundy">Admin Dashboard</h1>
                {user && <p className="text-xs text-healing-burgundy-light">Welcome, {user.name || user.username}</p>}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={loadAllData}
                disabled={refreshing}
                className="border-healing-border"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/')}
                className="border-healing-border"
                data-testid="view-site-btn"
              >
                <Home className="h-4 w-4 mr-2" />
                View Site
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleLogout}
                className="border-healing-border text-red-600 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DashboardStats stats={stats} />

        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="bg-white border border-healing-border">
            <TabsTrigger value="bookings" className="data-[state=active]:bg-healing-purple data-[state=active]:text-white">
              <Calendar className="h-4 w-4 mr-2" />
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="feedback" className="data-[state=active]:bg-healing-purple data-[state=active]:text-white">
              <MessageSquare className="h-4 w-4 mr-2" />
              Feedback ({feedback.length})
            </TabsTrigger>
            <TabsTrigger value="calendar" className="data-[state=active]:bg-healing-purple data-[state=active]:text-white">
              <Ban className="h-4 w-4 mr-2" />
              Block Calendar
            </TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-healing-purple data-[state=active]:text-white">
              <Clock className="h-4 w-4 mr-2" />
              My Schedule
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className="font-heading text-2xl text-healing-burgundy">Appointments</h2>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 border-healing-border" data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <strong>Booking Management:</strong> When you confirm a booking, the date/time will be automatically locked on the calendar. Clients won't be able to book that slot.
              </div>
            </div>

            {filteredBookings.length === 0 ? (
              <div className="admin-card text-center py-12">
                <Calendar className="h-12 w-12 text-healing-border mx-auto mb-4" />
                <p className="text-healing-burgundy-light">No bookings found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookings.map((booking) => (
                  <div 
                    key={booking.booking_id} 
                    className="admin-card"
                    data-testid={`booking-${booking.booking_id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="space-y-3 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-heading text-lg text-healing-burgundy">{booking.massage_type}</h3>
                          <Badge className={STATUS_COLORS[booking.status]}>{booking.status}</Badge>
                          <Badge className={PAYMENT_STATUS_COLORS[booking.payment_status]}>
                            {booking.payment_status}
                            {booking.payment_method && ` (${booking.payment_method})`}
                          </Badge>
                          {booking.status === 'confirmed' && (
                            <Badge className={booking.reminder_24h_sent ? 'bg-purple-100 text-purple-800 border-purple-200' : 'bg-gray-100 text-gray-500 border-gray-200'}>
                              {booking.reminder_24h_sent ? '24h Reminder Sent' : '24h Pending'}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-healing-burgundy-light">
                            <User className="h-4 w-4" />
                            {booking.client_name}
                          </div>
                          <div className="flex items-center gap-2 text-healing-burgundy-light">
                            <Mail className="h-4 w-4" />
                            <a href={`mailto:${booking.client_email}`} className="hover:text-healing-purple">
                              {booking.client_email}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-healing-burgundy-light">
                            <Phone className="h-4 w-4" />
                            <a href={`tel:${booking.client_phone}`} className="hover:text-healing-purple">
                              {booking.client_phone}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-healing-burgundy-light">
                            <DollarSign className="h-4 w-4" />
                            ${booking.price} ({booking.duration})
                          </div>
                          <div className="flex items-center gap-2 text-healing-burgundy-light">
                            <Calendar className="h-4 w-4" />
                            {booking.confirmed_date || booking.preferred_date}
                            {booking.confirmed_date && <Badge className="ml-1 bg-green-100 text-green-800">Locked</Badge>}
                          </div>
                          <div className="flex items-center gap-2 text-healing-burgundy-light">
                            <Clock className="h-4 w-4" />
                            {booking.confirmed_time || booking.preferred_time}
                          </div>
                        </div>

                        <div className="flex items-start gap-2 text-sm text-healing-burgundy-light">
                          <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>
                            {booking.location_type === 'mobile' 
                              ? `Mobile: ${booking.client_address}`
                              : 'Studio Location'}
                          </span>
                        </div>

                        {booking.notes && (
                          <p className="text-sm text-healing-burgundy-light bg-healing-cream-alt p-2 rounded">
                            Notes: {booking.notes}
                          </p>
                        )}

                        <p className="text-xs text-healing-burgundy-light">
                          Created: {new Date(booking.created_at).toLocaleString()}
                        </p>
                      </div>

                      <div className="flex flex-wrap lg:flex-col gap-2 lg:min-w-[140px]">
                        {booking.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => updateBookingStatus(booking.booking_id, 'confirmed')}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`confirm-${booking.booking_id}`}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateBookingStatus(booking.booking_id, 'cancelled')}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`cancel-${booking.booking_id}`}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          </>
                        )}
                        {booking.status === 'confirmed' && (
                          <Button
                            size="sm"
                            onClick={() => updateBookingStatus(booking.booking_id, 'completed')}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`complete-${booking.booking_id}`}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Complete
                          </Button>
                        )}
                        {booking.payment_status === 'unpaid' && booking.status !== 'cancelled' && (
                          <Select onValueChange={(method) => updatePaymentStatus(booking.booking_id, 'paid', method)}>
                            <SelectTrigger className="w-full border-healing-border" data-testid={`payment-${booking.booking_id}`}>
                              <SelectValue placeholder="Mark Paid" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="venmo">Venmo</SelectItem>
                              <SelectItem value="cashapp">Cash App</SelectItem>
                              <SelectItem value="zelle">Zelle</SelectItem>
                              <SelectItem value="cash">Cash</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`delete-booking-${booking.booking_id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Booking?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete this booking and unlock the time slot. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteBooking(booking.booking_id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback" className="space-y-4">
            <h2 className="font-heading text-2xl text-healing-burgundy">Client Feedback</h2>

            {feedback.length === 0 ? (
              <div className="admin-card text-center py-12">
                <MessageSquare className="h-12 w-12 text-healing-border mx-auto mb-4" />
                <p className="text-healing-burgundy-light">No feedback yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {feedback.map((item) => (
                  <div 
                    key={item.feedback_id} 
                    className="admin-card"
                    data-testid={`feedback-${item.feedback_id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-heading text-lg text-healing-burgundy">{item.client_name}</h3>
                        {item.client_email && (
                          <p className="text-sm text-healing-burgundy-light">{item.client_email}</p>
                        )}
                      </div>
                      <Badge className={item.is_approved ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {item.is_approved ? 'Visible' : 'Hidden'}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <span 
                          key={i} 
                          className={i < item.rating ? 'text-healing-purple' : 'text-healing-border'}
                        >
                          ★
                        </span>
                      ))}
                    </div>

                    <p className="text-healing-burgundy-light mb-3 italic">"{item.message}"</p>

                    {item.massage_type && (
                      <p className="text-sm text-healing-purple mb-3">{item.massage_type}</p>
                    )}

                    <p className="text-xs text-healing-burgundy-light mb-3">
                      {new Date(item.created_at).toLocaleString()}
                    </p>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleFeedbackApproval(item.feedback_id)}
                        className={item.is_approved 
                          ? "border-gray-300 text-gray-600" 
                          : "border-green-300 text-green-600 hover:bg-green-50"
                        }
                        data-testid={`toggle-feedback-${item.feedback_id}`}
                      >
                        {item.is_approved ? 'Hide' : 'Approve'}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`delete-feedback-${item.feedback_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Feedback?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete this feedback. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteFeedback(item.feedback_id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Calendar Blocking Tab */}
          <TabsContent value="calendar" className="space-y-6">
            <h2 className="font-heading text-2xl text-healing-burgundy">Block / Unblock Calendar</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Block Form */}
              <div className="admin-card space-y-4">
                <h3 className="font-heading text-lg text-healing-burgundy">Block a Day or Time Slot</h3>
                
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant={blockMode === 'day' ? 'default' : 'outline'}
                    onClick={() => { setBlockMode('day'); setBlockTime(''); setBlockTimeFrom(''); setBlockTimeTo(''); }}
                    className={blockMode === 'day' ? 'bg-healing-purple text-white' : 'border-healing-border'}
                    data-testid="block-mode-day">Day</Button>
                  <Button size="sm" variant={blockMode === 'time' ? 'default' : 'outline'}
                    onClick={() => { setBlockMode('time'); setBlockTimeFrom(''); setBlockTimeTo(''); }}
                    className={blockMode === 'time' ? 'bg-healing-purple text-white' : 'border-healing-border'}
                    data-testid="block-mode-time">Time Slot</Button>
                  <Button size="sm" variant={blockMode === 'range' ? 'default' : 'outline'}
                    onClick={() => { setBlockMode('range'); setBlockTime(''); }}
                    className={blockMode === 'range' ? 'bg-healing-purple text-white' : 'border-healing-border'}
                    data-testid="block-mode-range">Time Range</Button>
                  <Button size="sm" variant={blockMode === 'daterange' ? 'default' : 'outline'}
                    onClick={() => { setBlockMode('daterange'); setBlockDate(null); setBlockTime(''); setBlockTimeFrom(''); setBlockTimeTo(''); setBlockDateRange({ from: null, to: null }); }}
                    className={blockMode === 'daterange' ? 'bg-healing-purple text-white' : 'border-healing-border'}
                    data-testid="block-mode-daterange">Date Range</Button>
                  <Button size="sm" variant={blockMode === 'pickdays' ? 'default' : 'outline'}
                    onClick={() => { setBlockMode('pickdays'); setBlockDate(null); setBlockTime(''); setBlockTimeFrom(''); setBlockTimeTo(''); setBlockPickedDays([]); }}
                    className={blockMode === 'pickdays' ? 'bg-healing-purple text-white' : 'border-healing-border'}
                    data-testid="block-mode-pickdays">Pick Days</Button>
                  <Button size="sm" variant={blockMode === 'week' ? 'default' : 'outline'}
                    onClick={() => { setBlockMode('week'); setBlockTime(''); setBlockTimeFrom(''); setBlockTimeTo(''); }}
                    className={blockMode === 'week' ? 'bg-healing-purple text-white' : 'border-healing-border'}
                    data-testid="block-mode-week">Week</Button>
                  <Button size="sm" variant={blockMode === 'month' ? 'default' : 'outline'}
                    onClick={() => { setBlockMode('month'); setBlockTime(''); setBlockTimeFrom(''); setBlockTimeTo(''); }}
                    className={blockMode === 'month' ? 'bg-healing-purple text-white' : 'border-healing-border'}
                    data-testid="block-mode-month">Month</Button>
                </div>

                <p className="text-xs text-healing-burgundy-light">
                  {blockMode === 'day' && 'Block an entire day, or optionally select a time range'}
                  {blockMode === 'time' && 'Block a specific hour on a given day'}
                  {blockMode === 'range' && 'Block hours from Time A to Time B on a given day'}
                  {blockMode === 'daterange' && 'Select a start and end date — all days in between will be blocked'}
                  {blockMode === 'pickdays' && 'Click individual days on the calendar to select them, then block all at once'}
                  {blockMode === 'week' && 'Block all days of the selected week'}
                  {blockMode === 'month' && 'Block all days in the selected month'}
                </p>

                {/* Single date picker for day/time/range/week/month modes */}
                {['day', 'time', 'range', 'week', 'month'].includes(blockMode) && (
                  <div className="space-y-2">
                    <Label className="text-healing-burgundy">Select Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left border-healing-border" data-testid="block-date-picker">
                          {blockDate ? format(blockDate, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarPicker
                          mode="single"
                          selected={blockDate}
                          onSelect={setBlockDate}
                          disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {/* Optional time range for day mode */}
                {blockMode === 'day' && blockDate && (
                  <div className="space-y-3">
                    <Label className="text-healing-burgundy">Time Range <span className="text-xs text-healing-burgundy-light">(optional — leave empty to block entire day)</span></Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-healing-burgundy-light">From</Label>
                        <Select value={blockTimeFrom} onValueChange={setBlockTimeFrom}>
                          <SelectTrigger className="border-healing-border" data-testid="day-time-from">
                            <SelectValue placeholder="Start time" />
                          </SelectTrigger>
                          <SelectContent>
                            {ALL_TIME_SLOTS.map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-healing-burgundy-light">To</Label>
                        <Select value={blockTimeTo} onValueChange={setBlockTimeTo}>
                          <SelectTrigger className="border-healing-border" data-testid="day-time-to">
                            <SelectValue placeholder="End time" />
                          </SelectTrigger>
                          <SelectContent>
                            {ALL_TIME_SLOTS.filter(t => {
                              if (!blockTimeFrom) return true;
                              return ALL_TIME_SLOTS.indexOf(t) >= ALL_TIME_SLOTS.indexOf(blockTimeFrom);
                            }).map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {blockTimeFrom && blockTimeTo && (
                      <p className="text-xs text-healing-purple font-medium">
                        Will block {ALL_TIME_SLOTS.slice(ALL_TIME_SLOTS.indexOf(blockTimeFrom), ALL_TIME_SLOTS.indexOf(blockTimeTo) + 1).length} slot(s): {blockTimeFrom} - {blockTimeTo}
                      </p>
                    )}
                  </div>
                )}

                {/* Date Range picker */}
                {blockMode === 'daterange' && (
                  <div className="space-y-2">
                    <Label className="text-healing-burgundy">Select Date Range</Label>
                    <div className="border border-healing-border rounded-xl p-3 bg-healing-cream-alt" data-testid="block-daterange-calendar">
                      <CalendarPicker
                        mode="range"
                        selected={blockDateRange}
                        onSelect={(range) => setBlockDateRange(range || { from: null, to: null })}
                        numberOfMonths={2}
                        disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                        classNames={{
                          months: "flex flex-col sm:flex-row gap-3",
                          month: "flex-1",
                          caption: "flex justify-center pt-1 relative items-center mb-2",
                          caption_label: "font-heading text-sm text-healing-burgundy",
                          day_selected: "bg-healing-purple text-white",
                          day_range_middle: "bg-healing-purple/20 text-healing-burgundy",
                        }}
                      />
                    </div>
                    {blockDateRange.from && blockDateRange.to && (
                      <p className="text-xs text-healing-purple font-medium">
                        {format(blockDateRange.from, 'MMM d')} → {format(blockDateRange.to, 'MMM d, yyyy')}
                        {' '}({(() => {
                          let count = 0;
                          const c = new Date(blockDateRange.from);
                          while (c <= blockDateRange.to) { count++; c.setDate(c.getDate() + 1); }
                          return count;
                        })()} days)
                      </p>
                    )}
                  </div>
                )}

                {/* Pick multiple days */}
                {blockMode === 'pickdays' && (
                  <div className="space-y-2">
                    <Label className="text-healing-burgundy">Click days to select/deselect</Label>
                    <div className="border border-healing-border rounded-xl p-3 bg-healing-cream-alt" data-testid="block-pickdays-calendar">
                      <CalendarPicker
                        mode="multiple"
                        selected={blockPickedDays}
                        onSelect={(days) => setBlockPickedDays(days || [])}
                        numberOfMonths={2}
                        disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                        classNames={{
                          months: "flex flex-col sm:flex-row gap-3",
                          month: "flex-1",
                          caption: "flex justify-center pt-1 relative items-center mb-2",
                          caption_label: "font-heading text-sm text-healing-burgundy",
                          day_selected: "bg-healing-purple text-white rounded-full",
                        }}
                      />
                    </div>
                    {blockPickedDays.length > 0 && (
                      <p className="text-xs text-healing-purple font-medium">
                        {blockPickedDays.length} day(s) selected: {blockPickedDays.slice(0, 5).map(d => format(d, 'MMM d')).join(', ')}{blockPickedDays.length > 5 ? '...' : ''}
                      </p>
                    )}
                  </div>
                )}

                {/* Optional time range for bulk modes */}
                {['daterange', 'pickdays', 'week', 'month'].includes(blockMode) && (
                  <div className="space-y-3">
                    <Label className="text-healing-burgundy">Time Range <span className="text-xs text-healing-burgundy-light">(optional — leave empty to block entire day)</span></Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-healing-burgundy-light">From</Label>
                        <Select value={blockTimeFrom} onValueChange={setBlockTimeFrom}>
                          <SelectTrigger className="border-healing-border" data-testid="bulk-time-from">
                            <SelectValue placeholder="Start time" />
                          </SelectTrigger>
                          <SelectContent>
                            {ALL_TIME_SLOTS.map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-healing-burgundy-light">To</Label>
                        <Select value={blockTimeTo} onValueChange={setBlockTimeTo}>
                          <SelectTrigger className="border-healing-border" data-testid="bulk-time-to">
                            <SelectValue placeholder="End time" />
                          </SelectTrigger>
                          <SelectContent>
                            {ALL_TIME_SLOTS.filter(t => {
                              if (!blockTimeFrom) return true;
                              return ALL_TIME_SLOTS.indexOf(t) >= ALL_TIME_SLOTS.indexOf(blockTimeFrom);
                            }).map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {blockTimeFrom && blockTimeTo && (
                      <p className="text-xs text-healing-purple font-medium">
                        Will block {ALL_TIME_SLOTS.slice(ALL_TIME_SLOTS.indexOf(blockTimeFrom), ALL_TIME_SLOTS.indexOf(blockTimeTo) + 1).length} slot(s) per day: {blockTimeFrom} - {blockTimeTo}
                      </p>
                    )}
                  </div>
                )}

                {blockMode === 'time' && blockDate && (
                  <div className="space-y-2">
                    <Label className="text-healing-burgundy">Select Time Slot</Label>
                    <Select value={blockTime} onValueChange={setBlockTime}>
                      <SelectTrigger className="border-healing-border" data-testid="block-time-select">
                        <SelectValue placeholder="Choose a time" />
                      </SelectTrigger>
                      <SelectContent>
                        {ALL_TIME_SLOTS.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {blockMode === 'range' && blockDate && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-healing-burgundy">From</Label>
                        <Select value={blockTimeFrom} onValueChange={setBlockTimeFrom}>
                          <SelectTrigger className="border-healing-border" data-testid="block-time-from">
                            <SelectValue placeholder="Start time" />
                          </SelectTrigger>
                          <SelectContent>
                            {ALL_TIME_SLOTS.map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-healing-burgundy">To</Label>
                        <Select value={blockTimeTo} onValueChange={setBlockTimeTo}>
                          <SelectTrigger className="border-healing-border" data-testid="block-time-to">
                            <SelectValue placeholder="End time" />
                          </SelectTrigger>
                          <SelectContent>
                            {ALL_TIME_SLOTS.filter(t => {
                              if (!blockTimeFrom) return true;
                              return ALL_TIME_SLOTS.indexOf(t) >= ALL_TIME_SLOTS.indexOf(blockTimeFrom);
                            }).map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {blockTimeFrom && blockTimeTo && (
                      <p className="text-xs text-healing-purple font-medium">
                        Will block {ALL_TIME_SLOTS.slice(ALL_TIME_SLOTS.indexOf(blockTimeFrom), ALL_TIME_SLOTS.indexOf(blockTimeTo) + 1).length} slot(s): {blockTimeFrom} – {blockTimeTo}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-healing-burgundy">Reason (optional)</Label>
                  <Input
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="e.g., Holiday, Personal day"
                    className="border-healing-border"
                    data-testid="block-reason-input"
                  />
                </div>

                <Button
                  onClick={handleBlockSlot}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  disabled={
                    (blockMode === 'daterange' && (!blockDateRange.from || !blockDateRange.to)) ||
                    (blockMode === 'pickdays' && blockPickedDays.length === 0) ||
                    (!['daterange', 'pickdays'].includes(blockMode) && !blockDate)
                  }
                  data-testid="block-submit-btn"
                >
                  <Ban className="h-4 w-4 mr-2" />
                  {blockMode === 'day' && 'Block Entire Day'}
                  {blockMode === 'time' && 'Block Time Slot'}
                  {blockMode === 'range' && 'Block Time Range'}
                  {blockMode === 'daterange' && 'Block Date Range'}
                  {blockMode === 'pickdays' && `Block ${blockPickedDays.length} Selected Day(s)`}
                  {blockMode === 'week' && 'Block Entire Week'}
                  {blockMode === 'month' && 'Block Entire Month'}
                </Button>
              </div>

              {/* Blocked Slots List */}
              <div className="admin-card space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg text-healing-burgundy">
                    Currently Blocked ({blockedSlots.length})
                  </h3>
                  {blockedSlots.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={selectAllUnblock}
                        className="text-xs text-healing-purple hover:underline"
                        data-testid="select-all-unblock"
                      >
                        {selectedUnblockIds.length === blockedSlots.length ? 'Deselect All' : 'Select All'}
                      </button>
                      {selectedUnblockIds.length > 0 && (
                        <Button
                          size="sm"
                          onClick={handleBulkUnblock}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="bulk-unblock-btn"
                        >
                          <Unlock className="h-3 w-3 mr-1" />
                          Unblock {selectedUnblockIds.length}
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {blockedSlots.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="h-10 w-10 text-healing-border mx-auto mb-3" />
                    <p className="text-healing-burgundy-light text-sm">No blocked slots. All calendar days are open.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {blockedSlots.map((slot) => (
                      <div 
                        key={slot.slot_id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          selectedUnblockIds.includes(slot.slot_id) 
                            ? 'bg-green-50 border-green-300' 
                            : 'bg-red-50 border-red-200'
                        }`}
                        data-testid={`blocked-slot-${slot.slot_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUnblockIds.includes(slot.slot_id)}
                          onChange={() => toggleUnblockSelect(slot.slot_id)}
                          className="w-4 h-4 accent-green-600 flex-shrink-0"
                          data-testid={`unblock-check-${slot.slot_id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-body font-medium text-red-800 text-sm">
                            {slot.date} {slot.time ? `at ${slot.time}` : '(Entire Day)'}
                          </p>
                          {slot.reason && (
                            <p className="text-xs text-red-600 truncate">{slot.reason}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnblockSlot(slot.slot_id)}
                          className="border-green-300 text-green-700 hover:bg-green-50 flex-shrink-0"
                          data-testid={`unblock-${slot.slot_id}`}
                        >
                          <Unlock className="h-3 w-3 mr-1" />
                          Unblock
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* My Schedule Tab */}
          <TabsContent value="schedule" className="space-y-6">
            <ScheduleEditor
              schedule={schedule}
              onUpdateDay={updateScheduleDay}
              onSave={handleSaveSchedule}
              saving={savingSchedule}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
